import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  Flex,
  Select,
  useDisclosure,
} from "@chakra-ui/react";
import {
  getAllOrganizations,
  createCustomerOrganization,
  setOrganizationManagerHome,
} from "../../services/adminService";
import {
  getOrgRoleConfig,
  setOrgRoleConfig,
} from "../../services/organizationService";
import { useAdminData } from "../../hooks/useAdminData";
import { createToast } from "../../utils/toastUtils";
import { useUser } from "../../contexts/UserContext";

const OrganizationManagement = () => {
  const [creating, setCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const toastHelper = createToast(toast);
  const { userInfo } = useUser();
  const [savingManagerHomeId, setSavingManagerHomeId] = useState(null);

  // Role Config (JSON) panel — per-org view/edit of organizations.role_config.
  // Only one org's panel is open at a time (expandedOrgId), so the editor
  // state below is shared and reset whenever the open org changes.
  const [expandedOrgId, setExpandedOrgId] = useState(null);
  const [roleConfigText, setRoleConfigText] = useState("");
  const [roleConfigLoading, setRoleConfigLoading] = useState(false);
  const [roleConfigError, setRoleConfigError] = useState("");
  const [savingRoleConfig, setSavingRoleConfig] = useState(false);

  const toggleRoleConfig = async (orgId) => {
    // Clicking the open org's button collapses the panel.
    if (expandedOrgId === orgId) {
      setExpandedOrgId(null);
      return;
    }

    setExpandedOrgId(orgId);
    setRoleConfigError("");
    setRoleConfigText("");
    setRoleConfigLoading(true);
    try {
      const data = await getOrgRoleConfig(orgId);
      // Pretty-print; role_config may be null when the org has none set yet.
      setRoleConfigText(JSON.stringify(data.role_config ?? {}, null, 2));
    } catch (err) {
      setRoleConfigError(
        err.response?.data?.detail || "Failed to load role config"
      );
    } finally {
      setRoleConfigLoading(false);
    }
  };

  const handleSaveRoleConfig = async (orgId) => {
    // Validate the edited text is JSON before sending; surface parse errors inline.
    let parsed;
    try {
      parsed = JSON.parse(roleConfigText);
    } catch (err) {
      setRoleConfigError(`Invalid JSON: ${err.message}`);
      return;
    }

    setRoleConfigError("");
    setSavingRoleConfig(true);
    try {
      await setOrgRoleConfig(orgId, parsed);
      toastHelper.success("Success", "Role config saved");
    } catch (err) {
      const detail =
        err.response?.data?.detail || "Failed to save role config";
      setRoleConfigError(detail);
      toastHelper.error("Error", detail);
    } finally {
      setSavingRoleConfig(false);
    }
  };

  const fetchOrganizations = useCallback(() => {
    return getAllOrganizations();
  }, []);

  const {
    data: organizationsData,
    loading,
    error,
    refetch: refetchOrganizations,
  } = useAdminData(fetchOrganizations, []);

  const organizations = organizationsData?.organizations || [];

  const handleManagerHomeChange = async (orgId, managerHome) => {
    setSavingManagerHomeId(orgId);
    try {
      await setOrganizationManagerHome(orgId, managerHome);
      toastHelper.success("Success", "Manager home updated");
      await refetchOrganizations();
    } catch (err) {
      toastHelper.error(
        "Error",
        err.response?.data?.detail || "Failed to update manager home"
      );
    } finally {
      setSavingManagerHomeId(null);
    }
  };

  const handleCreateOrganization = async () => {
    if (!newOrgName.trim()) {
      toastHelper.error("Error", "Organization name is required");
      return;
    }

    setCreating(true);
    try {
      await createCustomerOrganization({ name: newOrgName.trim() });
      toastHelper.success("Success", "Organization created successfully");
      setNewOrgName("");
      onClose();
      refetchOrganizations();
    } catch (err) {
      toastHelper.error(
        "Error",
        err.response?.data?.detail || "Failed to create organization"
      );
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Text fontSize="sm" color="gray.500">Loading organizations...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <VStack spacing={5} align="stretch">
      {/* Toolbar */}
      <Flex justify="flex-end">
        <Button
          colorScheme="blue"
          size="sm"
          px={5}
          borderRadius="md"
          fontWeight="semibold"
          onClick={onOpen}
        >
          + Create Organization
        </Button>
      </Flex>

      {/* Table */}
      {organizations.length === 0 ? (
        <Box
          py={16}
          textAlign="center"
          bg="gray.50"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          borderStyle="dashed"
        >
          <Text color="gray.400" fontSize="sm">No organizations found</Text>
        </Box>
      ) : (
        <Box
          overflowX="auto"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          bg="white"
        >
          <Table variant="unstyled" size="sm">
            <Thead>
              <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">#</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Name</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Type</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Users</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Manager Home</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Role Config</Th>
              </Tr>
            </Thead>
            <Tbody>
              {organizations.map((org, idx) => (
                <React.Fragment key={org.id}>
                  <Tr
                    borderBottomWidth={idx < organizations.length - 1 ? "1px" : "0"}
                    borderBottomColor="gray.100"
                    _hover={{ bg: "gray.50" }}
                    transition="background 0.1s"
                  >
                    <Td py={3} px={4}>
                      <Text fontSize="xs" color="gray.400" fontFamily="mono">{org.id}</Text>
                    </Td>
                    <Td py={3} px={4}>
                      <Text fontSize="sm" fontWeight="semibold">{org.name}</Text>
                    </Td>
                    <Td py={3} px={4}>
                      <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2} fontSize="xs">
                        Organization
                      </Badge>
                    </Td>
                    <Td py={3} px={4}>
                      <HStack spacing={1} align="center">
                        <Text fontSize="sm" fontWeight="medium">{org.user_count || 0}</Text>
                        <Text fontSize="xs" color="gray.400">users</Text>
                      </HStack>
                    </Td>
                    <Td py={3} px={4}>
                      <Select
                        size="xs"
                        value={org.manager_home || "engagement"}
                        isDisabled={!userInfo?.can_switch_organizations || savingManagerHomeId === org.id}
                        onChange={(event) => handleManagerHomeChange(org.id, event.target.value)}
                        aria-label={`Manager home for ${org.name}`}
                      >
                        <option value="engagement">Engagement</option>
                        <option value="legacy_metrics">Advisor workspace</option>
                      </Select>
                    </Td>
                    <Td py={3} px={4}>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => toggleRoleConfig(org.id)}
                      >
                        {expandedOrgId === org.id ? "Hide" : "Role Config"}
                      </Button>
                    </Td>
                  </Tr>
                  {expandedOrgId === org.id && (
                    <Tr>
                      <Td colSpan={6} py={4} px={4} bg="gray.50">
                        <VStack align="stretch" spacing={3}>
                          <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                            Role Config (JSON)
                          </Text>
                          {roleConfigLoading ? (
                            <Text fontSize="sm" color="gray.500">Loading role config...</Text>
                          ) : (
                            <>
                              <Textarea
                                value={roleConfigText}
                                onChange={(e) => setRoleConfigText(e.target.value)}
                                fontFamily="mono"
                                fontSize="xs"
                                rows={12}
                                bg="white"
                                placeholder="{}"
                              />
                              {roleConfigError && (
                                <Alert status="error" borderRadius="md" fontSize="sm">
                                  <AlertIcon />
                                  {roleConfigError}
                                </Alert>
                              )}
                              <Flex justify="flex-end">
                                <Button
                                  colorScheme="blue"
                                  size="sm"
                                  onClick={() => handleSaveRoleConfig(org.id)}
                                  isLoading={savingRoleConfig}
                                  loadingText="Saving..."
                                >
                                  Save
                                </Button>
                              </Flex>
                            </>
                          )}
                        </VStack>
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Create Organization Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" boxShadow="xl">
          <ModalHeader
            pb={3} pt={6} px={6}
            fontSize="lg" fontWeight="semibold"
            borderBottomWidth="1px" borderBottomColor="gray.100"
          >
            Create New Organization
          </ModalHeader>
          <ModalCloseButton top={4} right={4} />
          <ModalBody px={6} py={5}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Organization Name</FormLabel>
              <Input
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="e.g., Acme Corp"
                onKeyDown={(e) => e.key === "Enter" && handleCreateOrganization()}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter
            px={6} py={4}
            borderTopWidth="1px" borderTopColor="gray.100"
            gap={2}
          >
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              colorScheme="blue"
              size="sm"
              px={6}
              onClick={handleCreateOrganization}
              isLoading={creating}
              loadingText="Creating..."
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default OrganizationManagement;
